import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Camera, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  LogIn,
  ArrowRight,
  User,
  Shield,
  Users,
  Crown,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext.jsx";
import SEOHead from "../components/SEOHead";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [loginType, setLoginType] = useState(null); // null, 'member', 'judge'
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [unverifiedUserEmail, setUnverifiedUserEmail] = useState(null);

  // Read login type from URL parameters
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'member' || typeParam === 'judge') {
      setLoginType(typeParam);
    }
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResendVerification = async () => {
    if (!unverifiedUserEmail) return;
    
    try {
      setIsResendingEmail(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-verification/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: unverifiedUserEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Verification email sent! Please check your inbox.');
        setErrors({});
        setUnverifiedUserEmail(null);
      } else {
        toast.error(data.detail || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      toast.error('Network error occurred');
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setErrors({});

    if (!formData.email || !formData.password) {
      return;
    }

    const result = await login(formData);
    if (result.success) {
      // Redirect based on login type and user role
      if (loginType === 'judge') {
        navigate("/judge");
      } else if (loginType === 'super-admin') {
        navigate("/system-login");
      } else {
        // Member login - check if profile is complete
        const user = result.user;
        
        // Check if email is verified first
        if (!user.email_verified) {
          // Email not verified - capture email for resend functionality
          setUnverifiedUserEmail(user.email);
          setErrors({ submit: "Please verify your email address before completing your profile. Check your inbox for a verification email." });
          return;
        }
        
        // Email verified - check if profile is complete
        // Consider profile complete if user has bio AND location
        const isProfileComplete = user.bio && user.bio.trim().length > 0 && 
                                 user.location && user.location.trim().length > 0;
        
        if (isProfileComplete) {
          // Profile complete - honor deep-link (e.g. finalist email -> /finalist-upload)
          const redirectTo = searchParams.get('redirect');
          navigate(redirectTo && redirectTo.startsWith('/') ? redirectTo : `/profile/${user.id}`);
        } else {
          // Profile incomplete - go to profile setup page
          navigate("/profile-setup");
        }
      }
    }
  };

  const resetLoginType = () => {
    setLoginType(null);
    setFormData({ email: "", password: "" });
  };

  // Login type selection screen
  if (!loginType) {
    return (
      <div className="min-h-screen pt-20 pb-20 bg-slate-900 md:bg-transparent flex items-center justify-center">
        <div className="max-w-md w-full px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <div className="bg-gradient-to-r from-orange-400 to-orange-600 p-3 rounded-lg">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">WUMA</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Choose Your Login Type
            </h1>
            <p className="text-orange-200">
              Select how you want to access the platform
            </p>
          </div>

          {/* Register Link - Moved Above */}
          <div className="text-center mb-6">
            <p className="text-orange-200">
              Need an account?{' '}
              <Link to="/register" className="text-orange-400 hover:text-orange-300 font-medium">
                Register as Member
                <ArrowRight className="w-4 h-4 ml-1 inline" />
              </Link>
            </p>
          </div>

          {/* Login Type Cards */}
          <div className="space-y-4">
            {/* Member Login */}
            <Card 
              className="bg-slate-800/50 border-orange-900/20 hover:border-orange-400/50 transition-all duration-300 cursor-pointer"
              onClick={() => setLoginType('member')}
            >
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-orange-500/20 p-3 rounded-full">
                    <User className="w-8 h-8 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-white text-xl font-semibold">Member Login</h3>
                    <p className="text-orange-200 text-sm">
                      Submit photos, view results, manage your profile
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-orange-400 ml-auto" />
                </div>
              </CardContent>
            </Card>

            {/* Judge Login */}
            <Card 
              className="bg-slate-800/50 border-orange-900/20 hover:border-orange-400/50 transition-all duration-300 cursor-pointer"
              onClick={() => setLoginType('judge')}
            >
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-yellow-500/20 p-3 rounded-full">
                    <Shield className="w-8 h-8 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-white text-xl font-semibold">Judge Login</h3>
                    <p className="text-orange-200 text-sm">
                      Access judging interface, review submissions
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-orange-400 ml-auto" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Login form (member or judge)
  return (
    <div className="min-h-screen pt-20 pb-20 bg-slate-900 md:bg-transparent flex items-center justify-center">
      <SEOHead 
        title="Login - World Underwater Macro Awards 2026"
        description="Login to your World Underwater Macro Awards account to submit photos, view your submissions, and participate in the competition."
      />
      <div className="max-w-md w-full px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="bg-gradient-to-r from-orange-400 to-orange-600 p-3 rounded-lg">
              {loginType === 'judge' ? <Shield className="w-8 h-8 text-white" /> : <Camera className="w-8 h-8 text-white" />}
            </div>
            <span className="text-2xl font-bold text-white">Underwater Macro Awards</span>
          </div>
          
          {/* Back button */}
          <Button 
            variant="ghost" 
            onClick={resetLoginType}
            className="text-orange-400 hover:text-orange-300 mb-4"
          >
            ← Back to login type selection
          </Button>

          <Badge variant="outline" className={`${
            loginType === 'super-admin' ? 'bg-red-900/50 text-red-300 border-red-400/50' :
            loginType === 'judge' ? 'bg-yellow-900/50 text-yellow-300 border-yellow-400/50' : 
            'bg-orange-900/50 text-orange-300 border-orange-400/50'
          } mb-4`}>
            {loginType === 'super-admin' ? <Shield className="w-4 h-4 mr-2" /> :
             loginType === 'judge' ? <Shield className="w-4 h-4 mr-2" /> : 
             <User className="w-4 h-4 mr-2" />}
            {loginType === 'super-admin' ? 'Super Admin Access' :
             loginType === 'judge' ? 'Judge Access' : 
             'Member Access'}
          </Badge>
          
          <h1 className="text-3xl font-bold text-white mb-2">
            {loginType === 'super-admin' ? '🔒 Super Admin Login' :
             loginType === 'judge' ? 'Judge Login' : 
             'Member Login'}
          </h1>
          <p className="text-orange-200">
            {loginType === 'super-admin' ? 'Access high-level competition management controls' :
             loginType === 'judge' ? 'Access the judging interface to review submissions' : 
             'Sign in to your member account'}
          </p>
        </div>

        {/* Login Form */}
        <Card className="bg-slate-800/50 border-orange-900/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">
              {loginType === 'judge' ? 'Judge Sign In' : 'Sign In'}
            </CardTitle>
            <CardDescription className="text-orange-200">
              Enter your credentials to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 bg-slate-700/50 border-orange-900/40 text-white placeholder-orange-300/50 focus:border-orange-400 focus:ring-orange-400"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 w-5 h-5" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-slate-700/50 border-orange-900/40 text-white placeholder-orange-300/50 focus:border-orange-400 focus:ring-orange-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-400 hover:text-orange-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Error Message */}
              {errors.submit && (
                <div className="bg-red-900/50 border border-red-400/50 rounded-lg p-4">
                  <p className="text-red-200 text-sm">{errors.submit}</p>
                  {/* Show Resend Verification button if user email is not verified */}
                  {unverifiedUserEmail && (
                    <div className="mt-3 pt-3 border-t border-red-400/30">
                      <Button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={isResendingEmail}
                        variant="outline"
                        className="w-full border-red-400 text-red-200 hover:bg-red-400 hover:text-white"
                        size="sm"
                      >
                        {isResendingEmail ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4 mr-2" />
                        )}
                        {isResendingEmail ? 'Sending...' : 'Resend Verification Email'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit"
                className={`w-full text-white py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${
                  loginType === 'judge' 
                    ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-700 hover:to-yellow-600'
                    : 'bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600'
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    {loginType === 'judge' ? 'Access Judge Dashboard' : 'Sign In'}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Info for Judges */}
        {loginType === 'judge' && (
          <Card className="bg-yellow-900/20 border-yellow-400/40 mt-6">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-yellow-200 font-medium mb-1">Judge Access</p>
                  <p className="text-yellow-300 text-sm">
                    You will access anonymized submissions organized by category and equipment type.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LoginPage;